import * as React from 'react';
import { cx } from '../utils';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

export type DividerOrientation = 'horizontal' | 'vertical';
export type DividerAs = 'div' | 'hr';

/* Divider primitive — visual rule line. Per WAI-ARIA 1.2 §separator
   the divider has no children and is not focusable (splitter variants
   would carry aria-valuenow/min/max, which Divider does not). The
   TypeScript surface OMITs the reserved-key set below at compile time;
   the runtime destructure plucks them out too so spread callers
   (`<Divider {...someBag} />`) can't bypass the contract either
   (codex round 2 + 3). */
type ReservedKey =
  | 'children'
  | 'role'
  | 'aria-orientation'
  | 'aria-hidden'
  | 'style'
  | 'className'
  /* dangerouslySetInnerHTML: React THROWS on <hr> (void element) and
     would smuggle children into the role="separator" branch under
     <div>. Codex round 3. */
  | 'dangerouslySetInnerHTML'
  /* tabIndex: a focusable separator is the splitter pattern, which
     requires aria-valuenow/min/max. Divider implements neither, so
     accepting tabIndex would invite A11y-invalid focusable rules. On
     `decorative` it also creates a focusable aria-hidden element,
     which fails axe `aria-hidden-focus`. Codex round 3. */
  | 'tabIndex'
  /* Related "content surface" props that contradict the no-children
     contract; stripped pre-emptively so the no-content invariant is
     uniform rather than appearing inconsistent across DOM features. */
  | 'contentEditable'
  | 'suppressContentEditableWarning'
  | 'draggable';

type ExtraAttrs = Omit<React.HTMLAttributes<HTMLElement>, ReservedKey>;

export interface DividerProps extends StyledProps, UnsafeStyleProps, ExtraAttrs {
  /* `aria-orientation` value emitted when not decorative; also drives
     the vertical CSS rule via the unconditional `data-orientation`
     attribute. Defaults to `horizontal` per WAI-ARIA 1.2 implicit. */
  orientation?: DividerOrientation;
  /* Rendered element. `<div>` (default) is parser-safe in any context
     including inside `<p>`; `<hr>` carries an implicit `separator` role
     so the explicit `role` attribute is dropped on that path. */
  as?: DividerAs;
  /* Per Radix Separator + WAI-ARIA 1.2 §separator: when the rule is
     purely visual and the surrounding structure already conveys the
     section break, opt in to `decorative` to drop `role="separator"`
     and emit `aria-hidden="true"`. AT will skip the element entirely. */
  decorative?: boolean;
}

/* Internal runtime-surface — DividerProps plus the reserved keys we
   actively strip if a caller injects them via spread. Compile-time
   callers never see these (they're Omit-ed from DividerProps); this
   wider shape is only consumed by the destructure inside the forwarded
   render function. */
type DividerInternalProps = DividerProps &
  Partial<Pick<React.HTMLAttributes<HTMLElement>, Exclude<ReservedKey, 'className'>>>;

export const Divider = React.forwardRef<HTMLElement, DividerProps>(function Divider(props, ref) {
  const {
    className,
    unsafe_style,
    orientation = 'horizontal',
    as = 'div',
    decorative = false,
    // Strip-and-ignore the reserved keys (see DividerInternalProps note).
    children: _children,
    role: _role,
    'aria-orientation': _ariaOrient,
    'aria-hidden': _ariaHidden,
    style: _style,
    dangerouslySetInnerHTML: _dsih,
    tabIndex: _tabIndex,
    contentEditable: _contentEditable,
    suppressContentEditableWarning: _scew,
    draggable: _draggable,
    ...rest
  } = props as DividerInternalProps;

  /* Mark the stripped-and-ignored locals as intentionally unused so
     TS noUnusedLocals / no-unused-vars don't complain; the destructure
     is the whole point. */
  void _children;
  void _role;
  void _ariaOrient;
  void _ariaHidden;
  void _style;
  void _dsih;
  void _tabIndex;
  void _contentEditable;
  void _scew;
  void _draggable;

  const ariaProps = decorative
    ? { 'aria-hidden': true as const }
    : as === 'hr'
      ? /* <hr> has implicit role="separator"; aria-orientation still
           communicates the axis to AT. */
        { 'aria-orientation': orientation }
      : { role: 'separator' as const, 'aria-orientation': orientation };

  const sharedProps = {
    ...rest,
    ...ariaProps,
    /* `data-orientation` is the styling hook — emitted unconditionally
       so the vertical CSS rule still applies in `decorative` mode (where
       `aria-orientation` is stripped). Matches Radix Separator. */
    'data-orientation': orientation,
    className: cx('j-divider', className),
    style: unsafe_style,
  };

  if (as === 'hr') {
    return <hr ref={ref as React.Ref<HTMLHRElement>} {...sharedProps} />;
  }
  return <div ref={ref as React.Ref<HTMLDivElement>} {...sharedProps} />;
});
